import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Suspense,
} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  FaSave,
  FaTimes,
  FaUserPlus,
  FaSearch,
  FaChevronDown,
  FaCalendarAlt,
  FaClock,
  FaMoneyBillWave,
  FaCoffee,
  FaBriefcase,
  FaRegCalendarCheck,
  FaListUl,
  FaSyncAlt,
  FaRupeeSign,
} from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

/* Lazy-load dialogs — they only mount on user action. */
const MdlSelectSingleStaffPayRoll = React.lazy(() =>
  import("../dialog/MdlSelectSingleStaffPayRoll")
);
const MdlSelectSession = React.lazy(() => import("../dialog/MdlSelectSession"));

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Page-local design tokens */
const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "rgba(148, 163, 184, 0.32)",
  borderStrong: "rgba(2, 136, 209, 0.35)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  ring: "rgba(3, 169, 244, 0.18)",
  danger: "#ef4444",
  success: "#10b981",
  radiusXl: 20,
  radiusLg: 16,
  radiusMd: 12,
  radiusSm: 10,
  shadowSoft: "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
  shadowCard: "0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 24px rgba(15, 23, 42, 0.05)",
  shadowLift: "0 1px 2px rgba(15, 23, 42, 0.05), 0 16px 48px rgba(15, 23, 42, 0.10)",
  heroGrad: (c) =>
    `linear-gradient(120deg, ${c.primary_color_shade9} 0%, ${c.primary_color_shade7} 45%, ${c.primary_color_shade5} 100%)`,
  ctaGrad: (c) =>
    `linear-gradient(135deg, ${c.primary_color_shade5} 0%, ${c.primary_color_shade7} 100%)`,
};

const SALARY_CIRCLE_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const WEEK_OFF_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const SALARY_TYPE_PALETTE = {
  Monthly: { bg: "rgba(99, 102, 241, 0.12)", color: "#4338ca" },
  Daily:   { bg: "rgba(16, 185, 129, 0.14)", color: "#047857" },
  Hourly:  { bg: "rgba(245, 158, 11, 0.16)", color: "#b45309" },
  default: { bg: "rgba(148, 163, 184, 0.18)", color: "#334155" },
};

function chipForSalaryType(t) {
  return SALARY_TYPE_PALETTE[t] || SALARY_TYPE_PALETTE.default;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateDMY(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString();
  }
  return String(value);
}

/* Scoped page CSS — focus ring, hover lift, skeleton shimmer. */
const PAGE_CSS = `
.proll-page :is(.form-control, .form-select) {
  border-radius: ${ui.radiusMd}px;
  border: 1px solid ${ui.border};
  background: ${ui.surface};
  padding: 10px 12px;
  font-size: 14px;
  color: ${ui.text};
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.proll-page :is(.form-control, .form-select):hover {
  border-color: rgba(2, 136, 209, 0.45);
}
.proll-page :is(.form-control, .form-select):focus {
  border-color: ${COLORS.primary_color_shade5};
  box-shadow: 0 0 0 4px ${ui.ring};
  outline: none;
}
.proll-page .input-affix { position: relative; }
.proll-page .input-affix .affix {
  position: absolute;
  inset: 0 auto 0 12px;
  display: inline-flex;
  align-items: center;
  color: ${ui.muted};
  pointer-events: none;
  font-size: 13px;
  font-weight: 700;
}
.proll-page .input-affix .form-control { padding-left: 32px; }
.proll-page .proll-row { transition: background .15s ease; }
.proll-page .proll-row:hover { background: ${ui.surfaceAlt}; }
.proll-page .proll-row td { border-color: ${ui.divider} !important; }
.proll-page .lift { transition: transform .15s ease, box-shadow .15s ease; }
.proll-page .lift:hover { transform: translateY(-1px); box-shadow: ${ui.shadowLift}; }
.proll-compact :is(.form-control, .form-select) {
  padding: 9px 12px;
  font-size: 15px;
  font-weight: 600;
  color: ${ui.text};
  border-radius: 10px;
  min-height: 42px;
  line-height: 1.25;
}
.proll-compact :is(.form-control, .form-select)::placeholder { color: #94a3b8; font-weight: 500; }
.proll-compact .input-affix .affix { font-size: 15px; color: ${ui.text}; font-weight: 700; }
.proll-compact .input-affix .form-control { padding-left: 30px; }
.proll-compact .form-label {
  margin-bottom: 4px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  color: ${ui.text} !important;
  letter-spacing: 0.01em;
}
.proll-compact .btn { padding: 9px 18px; font-size: 14px; font-weight: 700; }
/* Escape global ".btn { background-image: none !important; box-shadow: none !important; }". */
.proll-page .proll-cta {
  background-image: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%) !important;
  background-color: ${COLORS.primary_color_shade5} !important;
  box-shadow: 0 6px 18px rgba(3, 169, 244, 0.35) !important;
  color: #fff !important;
  border: 1px solid ${COLORS.primary_color_shade7} !important;
}
.proll-page .proll-cta:hover { filter: brightness(1.04); }
.proll-page .proll-table { font-size: 14px; }
.proll-page .proll-table thead th {
  padding: 12px 10px !important;
  background: ${ui.surfaceAlt};
  border-bottom: 1px solid ${ui.divider} !important;
  font-size: 11px !important;
  letter-spacing: 0.1em;
  color: ${ui.muted} !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  white-space: nowrap;
}
.proll-page .proll-table tbody td { padding: 12px 10px; border-color: ${ui.divider} !important; }
.proll-page .proll-table tbody tr { transition: background .15s ease; }
.proll-page .proll-table tbody tr:hover { background: rgba(3, 169, 244, 0.06); }
.proll-page .proll-search-wrap { position: relative; display: inline-flex; align-items: center; }
.proll-page .proll-search-wrap .proll-search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: rgba(255,255,255,0.75); font-size: 12px; pointer-events: none;
}
.proll-page .proll-search-wrap .proll-search-clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,0.18); border: 0;
  width: 20px; height: 20px; border-radius: 999px;
  color: #fff; font-size: 10px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.proll-page .proll-search {
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  border-radius: 999px;
  padding: 7px 28px 7px 30px;
  font-size: 13px;
  font-weight: 500;
  outline: none;
  min-width: 200px;
  backdrop-filter: blur(6px);
}
.proll-page .proll-search::placeholder { color: rgba(255,255,255,0.72); }
.proll-page .proll-search:focus {
  background: rgba(255,255,255,0.26);
  border-color: rgba(255,255,255,0.55);
  box-shadow: 0 0 0 4px rgba(255,255,255,0.12);
}
.proll-page .proll-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid ${ui.border};
  color: ${ui.textSoft};
  background: ${ui.surface};
  cursor: pointer;
  transition: all .15s ease;
  letter-spacing: 0.02em;
}
.proll-page .proll-chip:hover { border-color: ${COLORS.primary_color_shade5}; color: ${COLORS.primary_color_shade8}; }
.proll-page .proll-chip.active {
  background: ${COLORS.primary_color_shade5};
  color: #fff !important;
  border-color: ${COLORS.primary_color_shade5};
  box-shadow: 0 4px 12px rgba(3, 169, 244, 0.30);
}
.proll-page .proll-chip .proll-chip-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.22);
  color: ${ui.text};
  font-size: 10px; font-weight: 800;
}
.proll-page .proll-chip.active .proll-chip-count { background: rgba(255,255,255,0.28); color: #fff; }
.proll-page .proll-icon-btn {
  width: 34px; height: 34px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.32);
  backdrop-filter: blur(6px);
  cursor: pointer;
  transition: background .15s ease, transform .15s ease;
}
.proll-page .proll-icon-btn:hover:not(:disabled) { background: rgba(255,255,255,0.28); }
.proll-page .proll-icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.proll-page .proll-icon-btn.spinning svg { animation: proll-spin 1s linear infinite; }
@keyframes proll-spin { 100% { transform: rotate(360deg); } }
.proll-skeleton {
  position: relative;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.18);
  border-radius: 8px;
}
.proll-skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
  animation: proll-shimmer 1.2s infinite;
}
@keyframes proll-shimmer { 100% { transform: translateX(100%); } }
`;

const changeStaffBtnStyles = {
  card: {
    background: COLORS.primary_color_shade5,
    color: "#fff",
    borderRadius: 999,
    padding: "7px 16px",
    fontWeight: 700,
    fontSize: 13,
    boxShadow: "0 4px 14px rgba(3, 169, 244, 0.35)",
  },
  toolbar: {
    background: "transparent",
    color: COLORS.primary_color_shade6,
    fontWeight: 700,
    fontSize: 13,
  },
};

function ChangeStaffPickerButton({ onClick, variant }) {
  return (
    <button
      type="button"
      className="btn btn-sm border-0 lift"
      style={changeStaffBtnStyles[variant]}
      onClick={onClick}
    >
      <FaSearch className="me-1" />
      Change staff
    </button>
  );
}

const sessionBtnPressHandlers = {
  onMouseDown: (e) => { e.currentTarget.style.transform = "scale(0.985)"; },
  onMouseUp:   (e) => { e.currentTarget.style.transform = "scale(1)"; },
  onMouseLeave: (e) => { e.currentTarget.style.transform = "scale(1)"; },
};

function FieldLabel({ children, required, icon }) {
  return (
    <label
      className="form-label mb-1 d-inline-flex align-items-center gap-1"
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: ui.textSoft,
        letterSpacing: "0.01em",
      }}
    >
      {icon ? (
        <span style={{ color: COLORS.primary_color_shade6, fontSize: 12, display: "inline-flex" }}>
          {icon}
        </span>
      ) : null}
      <span>{children}</span>
      {required ? (
        <span aria-hidden style={{ color: ui.danger, marginLeft: 2, fontSize: 14, lineHeight: 1 }}>
          •
        </span>
      ) : null}
    </label>
  );
}

/* Hero/list decorative blobs (constant JSX, mounted once). */
const HERO_ORB_TOPRIGHT = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -60,
      top: -60,
      width: 220,
      height: 220,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
      filter: "blur(2px)",
    }}
  />
);
const HERO_ORB_BOTTOMLEFT = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      left: -40,
      bottom: -80,
      width: 180,
      height: 180,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.06)",
    }}
  />
);
const LIST_HEADER_ORB = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -40,
      top: -40,
      width: 160,
      height: 160,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.07)",
    }}
  />
);

function TableRowSkeleton({ cols = 8 }) {
  return (
    <tr>
      <td colSpan={cols} style={{ padding: 0 }}>
        <div className="d-flex align-items-center gap-2 py-2 px-1">
          <div className="proll-skeleton" style={{ width: 38, height: 38, borderRadius: 10 }} />
          <div className="flex-grow-1">
            <div className="proll-skeleton mb-1" style={{ width: "45%", height: 12 }} />
            <div className="proll-skeleton" style={{ width: "30%", height: 10 }} />
          </div>
          <div className="proll-skeleton d-none d-md-block" style={{ width: 60, height: 10 }} />
          <div className="proll-skeleton" style={{ width: 60, height: 10 }} />
          <div className="proll-skeleton" style={{ width: 70, height: 10 }} />
        </div>
      </td>
    </tr>
  );
}

const StaffRow = React.memo(function StaffRow({ s }) {
  const chip = chipForSalaryType(s.salary_type);
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              overflow: "hidden",
              border: `1px solid ${ui.border}`,
              flexShrink: 0,
              background: "#f8fafc",
            }}
          >
            <img
              src={s.staff_image_path || "/default-avatar.png"}
              alt=""
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
          <div className="d-flex flex-column min-w-0">
            <span
              className="fw-bold text-truncate"
              style={{ color: ui.text, fontSize: 14 }}
            >
              {s.staff_name}
            </span>
            <span
              className="text-truncate d-md-none"
              style={{ color: ui.muted, fontSize: 11 }}
            >
              {s.department_name || s.staff_role || "—"}
            </span>
          </div>
        </div>
      </td>
      <td className="d-none d-md-table-cell" style={{ color: ui.textSoft, fontWeight: 600 }}>
        {s.department_name || "—"}
      </td>
      <td className="d-none d-lg-table-cell" style={{ color: ui.muted }}>
        {s.staff_role || "—"}
      </td>
      <td>
        <span
          className="badge border-0"
          style={{
            background: chip.bg,
            color: chip.color,
            fontWeight: 700,
            fontSize: 11,
            padding: "5px 10px",
            borderRadius: 999,
            letterSpacing: "0.02em",
          }}
        >
          {s.salary_type || "—"}
        </span>
      </td>
      <td
        className="text-end fw-bold"
        style={{
          color: ui.text,
          fontVariantNumeric: "tabular-nums",
          fontSize: 14,
        }}
      >
        <span style={{ color: ui.muted, marginRight: 2, fontWeight: 500 }}>₹</span>
        {formatMoney(s.salary)}
      </td>
      <td
        className="d-none d-sm-table-cell"
        style={{ color: ui.textSoft, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
      >
        {s.working_hour === null || s.working_hour === undefined || s.working_hour === ""
          ? "—"
          : `${s.working_hour}h`}
      </td>
      <td className="d-none d-md-table-cell">
        <span
          className="badge border-0 d-inline-flex align-items-center gap-1"
          style={{
            background: "rgba(148, 163, 184, 0.18)",
            color: ui.textSoft,
            fontWeight: 600,
            fontSize: 11,
            padding: "5px 10px",
            borderRadius: 999,
          }}
        >
          <FaCoffee style={{ fontSize: 9 }} aria-hidden />
          {s.week_off || "—"}
        </span>
      </td>
      <td>
        <span
          className="d-inline-flex align-items-center gap-1"
          style={{
            color: ui.textSoft,
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
          }}
        >
          <FaCalendarAlt style={{ fontSize: 10, color: ui.muted }} aria-hidden />
          {formatDateDMY(s.joining_date)}
        </span>
      </td>
    </tr>
  );
});

const PayRollAddStaff = () => {
  const loggedIn = localStorage.getItem("token") !== null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [SESSION_ID, setSESSION_ID] = useState("");
  const [SESSION_NAME, setSESSION_NAME] = useState("");
  const [SESSION_STATUS, setSESSION_STATUS] = useState("");
  
  const [payrollStaffList, setPayrollStaffList] = useState([]);
  const [payrollStaffLoading, setPayrollStaffLoading] = useState(false);
  const [staffSearchTerm, setStaffSearchTerm] = useState("");
  const fetchRequestIdRef = useRef(0);

  /* On unmount, invalidate any in-flight fetch so it can't update state. */
  useEffect(() => {
    return () => {
      fetchRequestIdRef.current = -1;
    };
  }, []);
  
  const [staffFormData, setStaffFormData] = useState({
    staff_id: "",
    staff_name: "",
    designation: "",
    staff_image_path: "",
    department_name: "",
  });

  const [payrollFormData, setPayrollFormData] = useState({
    salary_type: "",
    salary: "",
    salary_circle: "",
    working_hour: "8",
    joining_date: "",
    week_off: "Sunday",
  });

  const fetchPayrollStaffList = useCallback((sessionId) => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = "getPayRollStaffList.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;

    const params = {
      school_code: SCHOOL_CODE,
      session_id: sessionId,
    };

    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).map(([k, v]) => [k, v != null ? String(v) : ""])
      )
    ).toString();
    const fullUrl = `${URL}?${queryString}`;
    console.log("getPayRollStaffList full URL (paste in browser):", fullUrl);

    const reqId = ++fetchRequestIdRef.current;
    const isCurrent = () => reqId === fetchRequestIdRef.current;

    setPayrollStaffLoading(true);
    axios
      .get(URL, { params })
      .then((res) => res.data)
      .then((response) => {
        if (!isCurrent()) return;
        console.log("getPayRollStaffList response:", response);
        if (response && response.api_status === "OK") {
          setPayrollStaffList(response.data || []);
        } else {
          setPayrollStaffList([]);
        }
      })
      .catch((err) => {
        if (!isCurrent()) return;
        console.error("getPayRollStaffList error:", err);
        setPayrollStaffList([]);
      })
      .finally(() => {
        if (!isCurrent()) return;
        setPayrollStaffLoading(false);
      });
  }, []);

  useEffect(() => {
    const mSession = SessionManager.getCurrentSession();
    if (mSession && mSession.length > 0) {
      setSESSION_ID(mSession[0].session_id);
      setSESSION_NAME(mSession[0].session_name);
      setSESSION_STATUS(mSession[0].status);
    }
  }, []);

  useEffect(() => {
    if (SESSION_ID) {
      fetchPayrollStaffList(SESSION_ID);
    }
  }, [SESSION_ID, fetchPayrollStaffList]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setPayrollFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setStaffFormData({
      staff_id: "",
      staff_name: "",
      designation: "",
      staff_image_path: "",
      department_name: "",
    });
    setPayrollFormData({
      salary_type: "",
      salary: "",
      salary_circle: "",
      working_hour: "8",
      joining_date: "",
      week_off: "Sunday",
    });
  }, []);

  const handleStaffSelection = useCallback((selectedStaff) => {
    setStaffFormData((prev) => ({
      ...prev,
      staff_id: selectedStaff.staff_id,
      staff_name: selectedStaff.staff_name,
      designation: selectedStaff.staff_role || "",
      staff_image_path: selectedStaff.staff_image_path || "",
      department_name: selectedStaff.department_name || "",
    }));
    setShowStaffModal(false);
    myToast.notify_success("Staff member selected successfully!");
  }, []);

  const handleOpenStaffModal = useCallback(() => {
    setShowStaffModal(true);
  }, []);

  const handleCloseStaffModal = useCallback(() => {
    setShowStaffModal(false);
  }, []);

  const ChangeSession = useCallback(() => setIsOpenSessionDialog(true), []);
  const HandleCloseSessionDialog = useCallback(() => setIsOpenSessionDialog(false), []);

  const HandleSelectedSession = useCallback(
    (session_id, session_name, session_status) => {
      setSESSION_ID(session_id);
      setSESSION_NAME(session_name);
      setSESSION_STATUS(session_status);
      setIsOpenSessionDialog(false);
      resetForm();
      fetchPayrollStaffList(session_id);
    },
    [resetForm, fetchPayrollStaffList]
  );

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const trimStr = (v) => String(v ?? "").trim();
    const staffId = trimStr(staffFormData.staff_id);
    const staffName = trimStr(staffFormData.staff_name);
    const designation = trimStr(staffFormData.designation);
    const salaryRaw = payrollFormData.salary;
    const hasSalary =
      salaryRaw !== "" &&
      salaryRaw !== null &&
      salaryRaw !== undefined &&
      !Number.isNaN(Number(salaryRaw));
    const joiningDate = trimStr(payrollFormData.joining_date);

    if (!SESSION_ID) {
      myToast.notify_error("Please select an academic session first");
      return;
    }
    if (!staffId || !staffName || !designation || !hasSalary || !joiningDate) {
      myToast.notify_error("Please select a staff member and fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const FUNCTION_NAME = "AddStaffPayRollDetails.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;

      const payload = {
        school_code: SCHOOL_CODE,
        session_id: SESSION_ID,
        ...staffFormData,
        ...payrollFormData,
      };

      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(payload).map(([k, v]) => [k, v != null ? String(v) : ""])
        )
      ).toString();
      const fullUrl = `${URL}?${queryString}`;
      console.log("Full URL (paste in browser):", fullUrl);

      const response = await axios.get(URL, { params: payload });

      if (response.data.api_status === "OK") {
        myToast.notify_success("Staff member added successfully!");
        resetForm();
        // refresh the already added staff list
        if (SESSION_ID) {
          fetchPayrollStaffList(SESSION_ID);
        }
      } else {
        myToast.notify_error(
          response.data.api_remark || "Failed to add staff member"
        );
      }
    } catch (error) {
      console.error("Error adding staff member:", error);
      myToast.notify_error("Error adding staff member. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [staffFormData, payrollFormData, SESSION_ID, fetchPayrollStaffList, resetForm]);

  const sessionButtonTitle = useMemo(
    () =>
      SESSION_NAME
        ? `${SESSION_NAME}${SESSION_STATUS ? ` (${SESSION_STATUS})` : ""}`
        : "Choose session",
    [SESSION_NAME, SESSION_STATUS]
  );

  const filteredPayrollStaffList = useMemo(() => {
    const q = staffSearchTerm.trim().toLowerCase();
    if (!q) return payrollStaffList;
    return payrollStaffList.filter((s) => {
      const hay = [s.staff_name, s.staff_id, s.staff_role, s.department_name]
        .map((v) => (v ?? "").toString().toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [payrollStaffList, staffSearchTerm]);

  const isPayrollListFiltered = Boolean(staffSearchTerm.trim());

  const hasStaffSelected = Boolean(staffFormData.staff_id);

  if (!loggedIn) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h2>Access Denied</h2>
            <p>You must be logged in to view this page.</p>
            <Link to="/Login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="content proll-page"
      style={{
        background: ui.pageBg,
        fontFamily: fontStack,
        color: ui.text,
        minHeight: "100%",
      }}
    >
      <style>{PAGE_CSS}</style>

      {/* Page header */}
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <span
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(3, 169, 244, 0.12)",
              color: COLORS.primary_color_shade6,
            }}
          >
            <FaUserPlus />
          </span>
          <span style={{ fontWeight: 800, fontSize: 16, color: ui.text }}>
            Add Staff to Payroll
          </span>
        </div>
        <div className="rightside-content-header d-flex align-items-center">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "16px",
          background: "transparent",
          boxShadow: "none",
        }}
      >
        <div
          className="payroll-table-container payroll-fade-in mb-4"
          style={{
            background: ui.surface,
            borderRadius: ui.radiusXl,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadowLift,
            overflow: "hidden",
            maxWidth: 1120,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Hero / Session + Select staff */}
          <div
            style={{
              background: ui.heroGrad(COLORS),
              padding: "16px 18px",
              borderBottom: `1px solid rgba(255,255,255,0.12)`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {HERO_ORB_TOPRIGHT}
            {HERO_ORB_BOTTOMLEFT}

            <div
              className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center justify-content-between gap-3 min-w-0"
              style={{ position: "relative" }}
            >
              <div className="d-flex align-items-center gap-3 text-white flex-shrink-0 min-w-0">
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <FaCalendarAlt style={{ fontSize: 16 }} aria-hidden />
                </span>
                <div className="min-w-0">
                  <div style={{ fontWeight: 800, fontSize: 11, letterSpacing: "0.14em", opacity: 0.85 }}>
                    ACADEMIC SESSION
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2, opacity: 0.95 }}>
                    Tap to change session
                  </div>
                </div>
              </div>

              <div
                className="d-flex flex-column flex-sm-row align-items-stretch gap-2 min-w-0 flex-grow-1 ms-lg-2"
                style={{ maxWidth: "100%", minWidth: 0 }}
              >
                <button
                  type="button"
                  onClick={ChangeSession}
                  className="d-flex align-items-center justify-content-between gap-2 min-w-0"
                  style={{
                    cursor: "pointer",
                    border: "none",
                    background: "rgba(255,255,255,0.96)",
                    color: COLORS.primary_color_shade9,
                    borderRadius: 999,
                    padding: "10px 16px",
                    minWidth: 0,
                    flex: "1 1 0%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    boxShadow: "0 6px 22px rgba(0,0,0,0.14)",
                    fontWeight: 600,
                    fontSize: 14,
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  }}
                  title={sessionButtonTitle}
                  {...sessionBtnPressHandlers}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0,
                      background:
                        SESSION_STATUS && /current|active/i.test(SESSION_STATUS)
                          ? ui.success
                          : COLORS.primary_color_shade5,
                      boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.18)",
                    }}
                  />
                  <span className="text-start text-truncate min-w-0" style={{ flex: "1 1 auto" }}>
                    {SESSION_NAME ? (
                      <>
                        {SESSION_NAME}
                        {SESSION_STATUS ? (
                          <span style={{ color: ui.muted, fontWeight: 500 }}>
                            {" "}· {SESSION_STATUS}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span style={{ color: ui.muted }}>Choose session…</span>
                    )}
                  </span>
                  <FaChevronDown style={{ opacity: 0.55, flexShrink: 0 }} aria-hidden />
                </button>

                <button
                  type="button"
                  onClick={handleOpenStaffModal}
                  className="d-inline-flex align-items-center justify-content-center gap-2 flex-shrink-0"
                  style={{
                    cursor: "pointer",
                    border: "1px solid rgba(255,255,255,0.5)",
                    background: "rgba(255,255,255,0.16)",
                    color: "#ffffff",
                    borderRadius: 999,
                    padding: "10px 18px",
                    fontWeight: 700,
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <FaSearch aria-hidden />
                  Select Staff
                </button>
              </div>
            </div>
          </div>

          <div style={{ padding: "16px 18px 20px" }}>
            {/* Selected staff summary */}
            {hasStaffSelected && (
              <div
                className="mb-2 lift"
                style={{
                  background: ui.surface,
                  border: `1px solid ${ui.border}`,
                  borderLeft: `4px solid ${COLORS.primary_color_shade5}`,
                  borderRadius: ui.radiusMd,
                  boxShadow: ui.shadowSoft,
                }}
              >
                <div className="px-3 py-3 d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 18,
                      overflow: "hidden",
                      padding: 3,
                      background: `linear-gradient(135deg, ${COLORS.primary_color_shade5}, ${COLORS.primary_color_shade8})`,
                      flexShrink: 0,
                      boxShadow: "0 6px 18px rgba(3, 169, 244, 0.38)",
                    }}
                  >
                    <img
                      src={staffFormData.staff_image_path || "/default-avatar.png"}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 15,
                        background: "#f8fafc",
                      }}
                    />
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div
                      className="fw-bold text-truncate"
                      style={{ color: ui.text, fontSize: "1.18rem", lineHeight: 1.25 }}
                    >
                      {staffFormData.staff_name}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "5px 12px",
                          borderRadius: 999,
                          background: "rgba(3, 169, 244, 0.12)",
                          color: COLORS.primary_color_shade8,
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {staffFormData.staff_id}
                      </span>
                      {staffFormData.designation ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 12px",
                            borderRadius: 999,
                            background: "rgba(148, 163, 184, 0.22)",
                            color: ui.text,
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          <FaBriefcase style={{ fontSize: 12 }} aria-hidden />
                          {staffFormData.designation}
                        </span>
                      ) : null}
                      {staffFormData.department_name ? (
                        <span
                          className="text-truncate"
                          style={{ color: ui.textSoft, fontWeight: 600, fontSize: 13 }}
                        >
                          · {staffFormData.department_name}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <ChangeStaffPickerButton variant="card" onClick={handleOpenStaffModal} />
                </div>
              </div>
            )}

            {/* Form or empty state */}
            {hasStaffSelected ? (
              <div
                style={{
                  background: ui.surface,
                  border: `1px solid ${ui.border}`,
                  borderRadius: ui.radiusMd,
                  boxShadow: ui.shadowSoft,
                  overflow: "visible",
                }}
              >
                <div
                  className="px-3 py-2 d-flex align-items-center"
                  style={{
                    background: `linear-gradient(180deg, ${ui.surfaceAlt} 0%, #f1f5f9 100%)`,
                    borderBottom: `1px solid ${ui.divider}`,
                    borderTopLeftRadius: ui.radiusMd,
                    borderTopRightRadius: ui.radiusMd,
                  }}
                >
                  <span
                    className="d-inline-flex align-items-center gap-2"
                    style={{ fontWeight: 800, fontSize: 15, color: COLORS.primary_color_shade8 }}
                  >
                    <FaMoneyBillWave aria-hidden style={{ fontSize: 16 }} />
                    Payroll details
                  </span>
                </div>

                <div className="proll-compact px-3 pt-2 pb-3">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-2">
                      <div className="col-12 col-sm-6 col-lg-4">
                        <FieldLabel required icon={<FaListUl />}>Salary type</FieldLabel>
                        <select
                          className="form-select"
                          name="salary_type"
                          value={payrollFormData.salary_type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select…</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Daily">Daily</option>
                          <option value="Hourly">Hourly</option>
                        </select>
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4">
                        <FieldLabel required icon={<FaRupeeSign />}>Salary</FieldLabel>
                        <div className="input-affix">
                          <span className="affix">₹</span>
                          <input
                            type="number"
                            className="form-control"
                            name="salary"
                            value={payrollFormData.salary}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4">
                        <FieldLabel icon={<FaCalendarAlt />}>Salary circle</FieldLabel>
                        <select
                          className="form-select"
                          name="salary_circle"
                          value={payrollFormData.salary_circle || ""}
                          onChange={handleInputChange}
                        >
                          <option value="">Day (1–31)</option>
                          {SALARY_CIRCLE_DAYS.map((d) => (
                            <option key={d} value={String(d)}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4">
                        <FieldLabel icon={<FaClock />}>Working hours</FieldLabel>
                        <input
                          type="number"
                          className="form-control"
                          name="working_hour"
                          value={payrollFormData.working_hour}
                          onChange={handleInputChange}
                          placeholder="8"
                          min="1"
                          max="24"
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4">
                        <FieldLabel required icon={<FaRegCalendarCheck />}>Joining date</FieldLabel>
                        <input
                          type="date"
                          className="form-control"
                          name="joining_date"
                          value={payrollFormData.joining_date}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4">
                        <FieldLabel icon={<FaCoffee />}>Week off</FieldLabel>
                        <select
                          className="form-select"
                          name="week_off"
                          value={payrollFormData.week_off}
                          onChange={handleInputChange}
                        >
                          {WEEK_OFF_DAYS.map((day) => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div
                      className="d-flex flex-wrap gap-2 align-items-center justify-content-end pt-2 mt-2"
                      style={{ borderTop: `1px solid ${ui.divider}` }}
                    >
                      <button
                        type="button"
                        className="btn"
                        style={{
                          borderRadius: 999,
                          fontWeight: 600,
                          border: `1px solid ${ui.border}`,
                          background: ui.surface,
                          color: ui.textSoft,
                        }}
                        onClick={resetForm}
                      >
                        <FaTimes className="me-1" />
                        Reset
                      </button>
                      <button
                        type="submit"
                        className="btn lift proll-cta"
                        style={{
                          borderRadius: 999,
                          fontWeight: 700,
                          minWidth: 160,
                        }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden
                            />
                            Saving…
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" aria-hidden />
                            Add Staff
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div
                style={{
                  background:
                    "radial-gradient(120% 120% at 50% 0%, rgba(3, 169, 244, 0.07) 0%, transparent 55%), " +
                    ui.surface,
                  border: `2px dashed ${ui.borderStrong}`,
                  borderRadius: ui.radiusMd,
                }}
              >
                <div className="py-4 px-4 d-flex flex-column flex-sm-row align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 18,
                      background: "rgba(3, 169, 244, 0.12)",
                      color: COLORS.primary_color_shade6,
                    }}
                  >
                    <FaUserPlus style={{ fontSize: 24 }} aria-hidden />
                  </div>
                  <div className="flex-grow-1 text-center">
                    <div style={{ fontWeight: 800, fontSize: 16, color: ui.text }}>
                      No staff selected yet
                    </div>
                    <div style={{ color: ui.muted, fontSize: 13 }}>
                      Open the picker and choose a staff member to configure their payroll.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenStaffModal}
                    className="btn lift proll-cta flex-shrink-0"
                    style={{
                      borderRadius: 999,
                      fontWeight: 700,
                      padding: "10px 18px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FaSearch className="me-2" aria-hidden />
                    Select Staff
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Already-in-salary-system list */}
          <div
            className="mt-2"
            style={{
              margin: "8px 18px 18px",
              background: ui.surface,
              border: `1px solid ${ui.border}`,
              borderRadius: ui.radiusLg,
              boxShadow: ui.shadowSoft,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade8} 0%, ${COLORS.primary_color_shade6} 100%)`,
                color: "#fff",
                padding: "14px 18px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {LIST_HEADER_ORB}
              <div
                className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-2"
                style={{ position: "relative" }}
              >
                <div className="d-flex align-items-center gap-3 min-w-0">
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.28)",
                      backdropFilter: "blur(6px)",
                      flexShrink: 0,
                    }}
                  >
                    <FaListUl style={{ fontSize: 15 }} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: "0.01em" }}>
                      Already in salary system
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                      {payrollStaffLoading
                        ? "Loading…"
                        : isPayrollListFiltered
                          ? `${filteredPayrollStaffList.length} of ${payrollStaffList.length} shown`
                          : `${payrollStaffList.length} total`}
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <div className="proll-search-wrap">
                    <FaSearch className="proll-search-icon" aria-hidden />
                    <input
                      type="text"
                      className="proll-search"
                      placeholder="Search name, ID, role…"
                      value={staffSearchTerm}
                      onChange={(e) => setStaffSearchTerm(e.target.value)}
                      aria-label="Search staff in salary system"
                    />
                    {staffSearchTerm ? (
                      <button
                        type="button"
                        className="proll-search-clear"
                        onClick={() => setStaffSearchTerm("")}
                        title="Clear search"
                        aria-label="Clear search"
                      >
                        <FaTimes aria-hidden />
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`proll-icon-btn ${payrollStaffLoading ? "spinning" : ""}`}
                    onClick={() => SESSION_ID && fetchPayrollStaffList(SESSION_ID)}
                    disabled={payrollStaffLoading || !SESSION_ID}
                    title="Refresh list"
                    aria-label="Refresh list"
                  >
                    <FaSyncAlt aria-hidden />
                  </button>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-2 py-2">
              {payrollStaffLoading ? (
                <div className="table-responsive">
                  <table className="table align-middle mb-0 proll-table">
                    <tbody>
                      {[0, 1, 2, 3].map((i) => <TableRowSkeleton key={i} />)}
                    </tbody>
                  </table>
                </div>
              ) : payrollStaffList.length === 0 ? (
                <div className="text-center py-5" style={{ color: ui.muted, fontSize: 14 }}>
                  <div
                    className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 18,
                      background: "rgba(148, 163, 184, 0.18)",
                      color: ui.muted,
                    }}
                  >
                    <FaListUl style={{ fontSize: 22 }} />
                  </div>
                  <div style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}>
                    No staff for this session yet
                  </div>
                  <div style={{ fontSize: 13 }}>
                    Add the first staff member above to get started.
                  </div>
                </div>
              ) : filteredPayrollStaffList.length === 0 ? (
                <div className="text-center py-5" style={{ color: ui.muted, fontSize: 14 }}>
                  <div
                    className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 18,
                      background: "rgba(148, 163, 184, 0.18)",
                      color: ui.muted,
                    }}
                  >
                    <FaSearch style={{ fontSize: 20 }} />
                  </div>
                  <div style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}>
                    No matching staff
                  </div>
                  <div style={{ fontSize: 13 }}>
                    Try a different search term.
                  </div>
                  <button
                    type="button"
                    onClick={() => setStaffSearchTerm("")}
                    className="btn mt-2"
                    style={{
                      borderRadius: 999,
                      fontWeight: 700,
                      fontSize: 13,
                      padding: "6px 14px",
                      border: `1px solid ${ui.border}`,
                      background: ui.surface,
                      color: ui.textSoft,
                    }}
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0 proll-table">
                    <thead>
                      <tr>
                        <th>Staff</th>
                        <th className="d-none d-md-table-cell">Dept</th>
                        <th className="d-none d-lg-table-cell">Role</th>
                        <th>Type</th>
                        <th className="text-end">Salary</th>
                        <th className="d-none d-sm-table-cell">Hrs</th>
                        <th className="d-none d-md-table-cell">Week Off</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayrollStaffList.map((s, idx) => (
                        <StaffRow key={`${String(s.staff_id)}-${idx}`} s={s} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals are lazy-loaded; render lazily only when open. */}
      <Suspense fallback={null}>
        {showStaffModal && (
          <MdlSelectSingleStaffPayRoll
            actionClose={handleCloseStaffModal}
            actionHandleSelected={handleStaffSelection}
            sessionId={SESSION_ID}
            sessionName={SESSION_NAME}
          />
        )}
        {isOpenSessionDialog && (
          <MdlSelectSession
            action={HandleCloseSessionDialog}
            action2={HandleSelectedSession}
          />
        )}
      </Suspense>
    </div>
  );
};

export default PayRollAddStaff;
